import { Color, ValueDisplayValue } from "@mcleod/core";
import { ClickEvent } from "../../events/ClickEvent";
import { Component } from "../..";
import { ComponentStyles } from "../../base/ComponentStyles";
import { Event } from "../../events/Event";
import { KeyEvent } from "../../events/KeyEvent";
import { Label } from "../label/Label";
import { RadioOptionPropDefinitions, RadioOptionProps } from "./RadioOptionProps";
import { RadioGroup } from "./RadioGroup";

export abstract class RadioOption extends Component implements RadioOptionProps {
    private _labelDisplayValue: Label;
    private _group: RadioGroup;
    private _valueDisplayValue: ValueDisplayValue;

    constructor(props?: Partial<RadioOptionProps>) {
        super("div", props);
        this.createDisplayValueLabel();
        this.addCaptionLabelElement();
        this.setProps({ rowBreak: false, themeKey: "radioOption.base", ...props });
        this.setAutomationStateAttribute(false);
    }

    abstract get shortName(): string;
    abstract enable(): void;
    abstract disable(): void;
    override getFocusTarget(): HTMLElement {
        return null;
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.labelDisplayValue, "labelDisplayValue");
    }

    get labelDisplayValue(): Label {
        return this._labelDisplayValue;
    }

    private set labelDisplayValue(value: Label) {
        this._labelDisplayValue = value;
    }

    protected createDisplayValueLabel() {
        this.labelDisplayValue = new Label({ themeKey: "radioOption.displayValue" });
        this.labelDisplayValue.addClickListener((event: ClickEvent) => {
            this.userSelected(event);
            if (this.focusable)
                this.focus();
            if (this._designer != null)
                event.shouldAutomaticallyStopPropagation = false;
        });
    }

    private addCaptionLabelElement() {
        this._element.appendChild(this.labelDisplayValue._element);
    }

    public get valueDisplayValue(): ValueDisplayValue {
        return this._valueDisplayValue;
    }

    public set valueDisplayValue(value: ValueDisplayValue) {
        this._valueDisplayValue = value;
        this.labelDisplayValue.text = this.valueDisplayValue.displayValue;
        this.syncInnerComponentIds();
    }

    public get displayValue(): string {
        return this.valueDisplayValue?.displayValue;
    }

    public get value(): any {
        return this.valueDisplayValue?.value;
    }

    get valueAsString(): string {
        return this.value;
    }

    get group(): RadioGroup {
        return this._group;
    }

    set group(value: RadioGroup) {
        this._group = value;
        this.syncInnerComponentIds();
    }

    protected userSelected(event: Event) {
        const newSelection = this.resolveNewSelection();
        if (this.focusable && this.enabled && this._designer == null)
            this.group.setSelectedOption(newSelection, event);
        if (this._designer != null)
            event.shouldAutomaticallyStopPropagation = false;
    }

    private resolveNewSelection(): RadioOption {
        if (this.group.allowIndeterminate === true && this.group.selectedOption === this)
            return null;
        return this;
    }

    set selected(value: boolean) {
        this.setAutomationStateAttribute(value);
    }

    get focusable(): boolean {
        return this.group?.focusable;
    }

    protected handleSelectionViaKey(event: KeyEvent) {
        if (event.key === ' ' && this.enabled && this.__designer == null) {
            this.userSelected(event);
            event.consume();
        }
    }

    override _applyEnabled(value: boolean): void {
        (value === true) ? this.enable() : this.disable();
    }

    protected override _syncVisible() {
        const collapseStyle = this.preventCollapse ? ComponentStyles.hiddenNoCollapse : ComponentStyles.hidden;
        this.setClassIncluded(collapseStyle, !this.visible);
    }

    private setAutomationStateAttribute(selected: boolean) {
        this._element.setAttribute("automation-state", selected.toString());
    }

    public override get color(): Color {
        return super.color;
    }

    public override set color(value: Color) {
        super.color = value;
        this.labelDisplayValue.color = value;
    }

    override getPropertyDefinitions() {
        return RadioOptionPropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "radiobutton";
    }

    override get properName(): string {
        return "Radio Item";
    }

    override getBasicValue(): any {
        return this.value;
    }
}
